import Marker, { Marker2 } from './Marker'
import Traveler, { Traveler2 } from './Traveler'
import Note from './Note'
import ItineraryGroup from './ItineraryGroup'
import ItinerarySpot, { ItinerarySpot2 } from './ItinerarySpot'
import Destination, { Destination2 } from './Destination'
import Spot, { Spot2 } from './Spot'
import BookMark from './BookMark'
import Agency from './Agency'
import ItineraryCategory from './ItineraryCategory'
import { serialize } from './helpers/serialize'
import Media from './Media'
import { InspirationSource } from './Guide'

export type AccessType = undefined | 'code'

export enum TRIP_ACTIONS {
	view = 'GET',
	edit = 'PATCH',
	delete = 'DELETE',
}

export enum TRIP_ROLES {
	owner = 'owner',
	manager = 'manager',
	viewer = 'viewer',
}

export enum TRIP_TASK_STATE {
	todo = 'todo',
	doing = 'doing',
	done = 'done',
}

export type TripTask = {
	id?: string
	title: string
	description?: string
	dueDate?: Date
	state: TRIP_TASK_STATE
	modified?: string
	assignees?: Traveler2[]
	assigneesCount?: number
}

export type TripOptions = {
	actions: TRIP_ACTIONS[]
	bookings: boolean
	checklist: boolean
	discover: boolean
	name: string
	role: TRIP_ROLES
	trip: boolean
	isRemovable: boolean
}

export type ItineraryStatus =
	| 'previous'
	| 'ongoing'
	| 'following'
	| 'all'
	| 'own'
	| 'next'

export enum TRIP_PHASE {
	ALL = 'all',
	CURRENT = 'current',
	NEXT = 'future',
	PAST = 'past',
	NO_DATES = 'no_dates',
}

export type Itinerary2 = {
	id: string | null
	title?: string
	subtitle?: string
	description?: string
	referenceType: 'itinerary'
	access?: AccessType
	travelersPictures?: string[]
	numDays?: number
	numNotes?: number
	numTravelers?: number
	startDate?: string
	endDate?: string
	name?: string
	numDestinations?: number
	destinations?: Destination2[]
	destination?: Destination2
	destinationsNextPage?: number
	numSpots?: number
	isPrivate?: boolean
	covers?: string[]
	invitationCode?: string
	markers?: Marker2[]
	spots?: ItinerarySpot2[]
	spotsNextPage?: number
	travelers?: Traveler[]
	notes?: Note[]
	notesNextPage?: number
	isEditable?: boolean
	isVisible?: boolean
	isRemovable?: boolean
	isTrip?: boolean
	isDiscover?: boolean
	isBookings?: boolean
	groups?: ItineraryGroup[]
	spotsGroupId?: string | null
	recommendedSpots?: Spot2[]
	recommendedSpotsNextPage?: number
	blogs?: BookMark[]
	videos?: BookMark[]
	role?: string
	agencyId?: string
	agency?: Agency
	categories?: ItineraryCategory[]
	cover?: string | null
	coverId?: string
	coverMedia?: Media
	isUserCover?: boolean
	creatorId?: string | null
	creator?: Traveler | null
	numViews?: number
	phase?: TRIP_PHASE
	declaredTravelers?: number
	potentialTravelers?: number
	enterpriseLogo?: string
	inspirationSource?: InspirationSource
}

export default class Itinerary {
	id: string | null
	title?: string
	referenceType?: string
	access?: AccessType
	travelersPictures?: string[]
	numDays?: number
	numNotes?: number
	numTravelers?: number
	startDate?: string
	endDate?: string
	name?: string
	numDestinations?: number
	destinations?: Destination[]
	destinationsNextPage?: number
	numSpots?: number
	isPrivate?: boolean
	covers?: string[]
	invitationCode?: string
	markers?: Marker[]
	spots?: ItinerarySpot[]
	spotsNextPage?: number
	travelers?: Traveler[]
	notes?: Note[]
	notesNextPage?: number
	isEditable?: boolean
	isVisible?: boolean
	isRemovable?: boolean
	isTrip?: boolean
	isDiscover?: boolean
	isBookings?: boolean
	groups?: ItineraryGroup[]
	spotsGroupId?: string | null
	recommendedSpots?: Spot[]
	recommendedSpotsNextPage?: number
	blogs?: BookMark[]
	videos?: BookMark[]
	role?: string
	agencyId?: string
	agency?: Agency
	categories?: ItineraryCategory[]
	cover?: string | null
	creatorId?: string | null
	creator?: Traveler | null
	numViews?: number
	constructor(itinerary?: Itinerary) {
		this.id = itinerary?.id || null
		this.referenceType = 'itinerary'
		this.access = itinerary?.access
		this.travelersPictures = itinerary?.travelersPictures || []
		this.numDays = itinerary?.numDays
		this.numNotes = itinerary?.numNotes
		this.numTravelers = itinerary?.numTravelers || 1
		this.startDate = itinerary?.startDate
		this.endDate = itinerary?.endDate
		this.name = itinerary?.name || ''
		this.numDestinations = itinerary?.numDestinations || 0
		this.destinations = serialize(Destination, itinerary?.destinations)
		this.destinationsNextPage = itinerary?.destinationsNextPage
		this.numSpots = itinerary?.numSpots || 0
		this.isPrivate =
			itinerary?.isPrivate === undefined ? false : itinerary.isPrivate
		this.covers = itinerary?.covers || []
		this.invitationCode = itinerary?.invitationCode
		this.markers = serialize(Marker, itinerary?.markers)
		this.spots = serialize(ItinerarySpot, itinerary?.spots)
		this.spotsNextPage = itinerary?.spotsNextPage
		this.travelers = serialize(Traveler, itinerary?.travelers)
		this.notes = serialize(Note, itinerary?.notes)
		this.notesNextPage = itinerary?.notesNextPage
		this.isEditable = itinerary?.isEditable
		this.isVisible = itinerary?.isVisible
		this.isRemovable = itinerary?.isRemovable
		this.isTrip = itinerary?.isTrip
		this.isDiscover = itinerary?.isDiscover
		this.isBookings = itinerary?.isBookings
		this.groups = serialize(ItineraryGroup, itinerary?.groups)
		this.spotsGroupId = itinerary?.spotsGroupId || null
		this.recommendedSpots = serialize(Spot, itinerary?.recommendedSpots)
		this.recommendedSpotsNextPage = itinerary?.recommendedSpotsNextPage
		this.blogs = serialize(BookMark, itinerary?.blogs)
		this.videos = serialize(BookMark, itinerary?.videos)
		this.role = itinerary?.role
		this.agencyId = itinerary?.agencyId
		this.agency = serialize(Agency, itinerary?.agency)
		this.categories = serialize(ItineraryCategory, itinerary?.categories)
		this.cover = itinerary?.cover || null
		this.creatorId = itinerary?.creatorId || null
		this.creator = serialize(Traveler, itinerary?.creator || null)
		this.numViews = itinerary?.numViews || 0
	}
}

export interface ItineraryForm {
	id: string | null
	name?: string
	startDate?: string | null
	endDate?: string | null
	isPrivate: boolean
	showDate: boolean
	destination?: Destination2
	option?: string
	numDays?: number
	categoryIds?: string[]
	coverId: string | undefined | null
	recommendedSpots?: boolean
	origin?: string
}
